"use client"
import { useContext, type ReactNode } from "react"
import {
  HoverSwapFadeOverlay,
  hoverSwapHiddenContentStyle,
  useHoverSwapSequentialFade,
} from "./hoverSwapFade"
import { PolicyHoverDetail, usePolicyHoverSwap } from "./policy/policyHoverSwap"
import {
  POLICY_STATUS_LABELS,
  POLICY_STATUS_ORDER,
  type PolicyStatus,
} from "./policy/policiesData"
import { useGroupedPoliciesForActiveDate } from "./policy/useGroupedPoliciesForActiveDate"
import { PolicyMapHoverDetail } from "./policy/policyMapHoverDetail"
import { useLaborHoverSwap } from "./labor/laborHoverSwap"
import {
  MetricHoverDetailContent,
  useMetricHoverSwap,
} from "./metrics/metricHoverSwap"
import {
  CappedLabList,
  POWER_BREAKDOWN_HIERARCHY_STYLE,
  POWER_COMBO_MAX_LAB_ROWS,
  POWER_HOVER_TITLE,
  RegionGroupBlock,
  usePowerHoverSwap,
  type PowerBreakdownMetricLabel,
  type PowerFrontierSplitDesign,
} from "./power/powerHoverSwap"
import { usePowerRegions } from "./power/distribution-of-power-bubbles/usePowerRegions"
import { formatCompact } from "../../plan-a/economic-data/calculations"
import {
  type PowerLabData,
  type PowerLabId,
  type PowerRegionData,
  type PowerRegionId,
} from "../../power-bubble-chart/powerBubbleChartData"
import {
  genericFrontierLabIcon,
  labIconById,
} from "../../power-bubble-chart/powerChartIcons"
import {
  HUD_HOVER_RULE_CENTERED_TOP_PX,
  HudHoverClickHint,
  HudHoverDescription,
  HudHoverDetailBody,
  HudHoverDetailRule,
  HudHoverRuleTreatmentContext,
  HudHoverTitle,
} from "./hudHoverDetailHeader"
import { useHoverDetailViewportRowCap } from "./useHoverDetailViewportRowCap"
import {
  getHoveredRegionId,
  type HoveredPowerItem,
} from "../../power-bubble-chart/hoveredPowerItem"

// ─── Editable content ────────────────────────────────────────────────────────
export const POLICY_LIST_EMPTY_SECTION_MARK = "—"

// Every hover detail in the below-HUD slot (power breakdown, policy detail,
// by-section policy list, metric definitions) renders this many px ABOVE the
// labor strip's top edge. Pure visual nudge (transform) — the slot's layout
// height is unchanged. STOCK rule treatment only.
export const BELOW_HUD_DETAIL_RAISE_PX = 5

// Non-stock treatments: the detail's content (title tier and everything
// below) sits this many px below the labor strip's top edge — flush read too
// tight under the hung rule (Joanna 2026-07-11). The rule itself stays put.
export const BELOW_HUD_DETAIL_TOP_NUDGE_PX = 3

// Copy for naming the power breakdown's METRIC (H100-equivalent compute) —
// one tab per approach, picked by PowerHoverSwap's breakdownMetricLabel:
export const POWER_METRIC_COPY = {
  // "units-header": the old floating tooltip's column-header row.
  unitsHeaderName: "Name",
  unitsHeaderUnit: "H100 equivalents",
  // "title-units": rides the title line's right edge.
  titleUnits: "in H100 equivalents",
  // "definition": sans one-liner under the title (the ladder's subtitle voice).
  definition:
    "Who holds the world's frontier AI compute, measured in H100-equivalent chips.",
  // "measured-inline": the short line in the definition-inline position
  // (title row, right-aligned).
  measuredInline: "Measured in H100-equivalent chips.",
  // "value-units": unit suffix after each region total.
  valueUnitSuffix: "H100e",
  // "footnote": tiny mono line under the columns.
  footnote: "All figures in H100-equivalent chips.",
}
// The label variants that put a sentence UNDER the breakdown title, and
// which POWER_METRIC_COPY sentence each one shows. Membership here is what
// turns the below-title row on (the map keys double as the guard).
const POWER_METRIC_BELOW_TITLE_COPY: Partial<
  Record<PowerBreakdownMetricLabel, keyof typeof POWER_METRIC_COPY>
> = {
  definition: "definition",
  "definition-italic": "definition",
  "definition-measure": "definition",
  "definition-nowrap": "definition",
  "measured-below": "measuredInline",
}

// Every policy under its section, as three side-by-side status columns —
// shown while a status LABEL is hovered, with the hovered section at full
// strength and the others dimmed (like unhovered power-breakdown regions).
// Section headers echo the power breakdown's region headers, policy names
// its lab rows.
const PoliciesBySectionColumns = ({
  hoveredStatus,
}: {
  hoveredStatus: PolicyStatus
}) => {
  const grouped = useGroupedPoliciesForActiveDate()
  return (
    <div className="flex gap-3">
      {POLICY_STATUS_ORDER.map((status) => (
        <div
          key={status}
          className={`flex-1 min-w-0 ${status === hoveredStatus ? "" : "opacity-[0.55]"}`}
        >
          <div className="font-mono font-bold text-[11px]">
            {POLICY_STATUS_LABELS[status]}
          </div>
          <div className="mt-0.5">
            {grouped[status].length === 0 ? (
              <div className="font-mono opacity-40 text-[8.5px] leading-[1.5]">
                {POLICY_LIST_EMPTY_SECTION_MARK}
              </div>
            ) : (
              grouped[status].map((policy) => (
                <div key={policy.id} className="font-mono text-[8.5px] leading-[1.5]">
                  {policy.label}
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// The distribution-of-power breakdown as THREE side-by-side region columns
// (America / China / Rest of World), for the below-the-HUD detail slot —
// the full HUD width is available there, so nothing needs to stack or scoot.
// The hovered cell's region highlights, the others dim, like the tab-15
// takeover.
const PowerHoverThreeColumnBreakdown = ({
  hoveredItem,
}: {
  hoveredItem: HoveredPowerItem
}) => {
  const regions = usePowerRegions()
  // The hovered-region emphasis treatment and the metric-label approach,
  // set per-variant on the PowerHoverSwap provider (see
  // POWER_BREAKDOWN_EMPHASIS_STYLE / POWER_METRIC_COPY).
  const swap = usePowerHoverSwap()
  const emphasis = swap?.breakdownRegionEmphasis
  const metricLabel = swap?.breakdownMetricLabel
  const itemHighlightDisabled = swap?.breakdownItemHighlightDisabled
  const hierarchy = swap?.breakdownHierarchy
  // On short viewports the columns truncate early enough that every
  // column's "And N more" line — and the "Click for details" hint below the
  // columns — still fits on screen. The reserve covers the hint's 18px gap
  // + its line + breathing room to the viewport edge; the -1 keeps a slot
  // for the "And N more" line itself.
  const { containerRef, rowCap } = useHoverDetailViewportRowCap({
    enabled: true,
    rowSelector: "[data-power-lab-row]",
    bottomReservePx: 44,
  })
  const maxLabRows = Math.max(
    2,
    Math.min(POWER_COMBO_MAX_LAB_ROWS, (rowCap ?? Infinity) - 1),
  )
  return (
    <div ref={containerRef}>
      <div className="flex items-baseline justify-between gap-3">
        <HudHoverTitle className="shrink-0">{POWER_HOVER_TITLE}</HudHoverTitle>
        {metricLabel === "title-units" && (
          <div className="font-mono whitespace-nowrap text-[9px]">
            {POWER_METRIC_COPY.titleUnits}
          </div>
        )}
        {/* "definition-inline" / "measured-inline": the sentence sits
            right-aligned on the title line, wrapping within a share of the
            row so it never crowds the title. */}
        {(metricLabel === "definition-inline" || metricLabel === "measured-inline") && (
          <div className="font-sans text-right text-[9.5px] leading-[1.5] tracking-[0.01em] max-w-[58%]">
            {metricLabel === "measured-inline"
              ? POWER_METRIC_COPY.measuredInline
              : POWER_METRIC_COPY.definition}
          </div>
        )}
      </div>
      {/* The below-title sentence. "definition-measure" caps it to a reading
          measure (em units — tracks the font size) instead of running the
          full HUD width. */}
      {metricLabel !== undefined && metricLabel in POWER_METRIC_BELOW_TITLE_COPY && (
        <HudHoverDescription
          className={`${metricLabel === "definition-italic" ? "italic" : ""} ${
            metricLabel === "definition-measure" ? "max-w-[36em]" : ""
          } ${metricLabel === "definition-nowrap" ? "whitespace-nowrap" : ""}`}
        >
          {POWER_METRIC_COPY[POWER_METRIC_BELOW_TITLE_COPY[metricLabel]!]}
        </HudHoverDescription>
      )}
      {metricLabel === "units-header" && (
        <div className="flex items-baseline justify-between gap-3 font-mono font-bold mt-1 pb-0.5 border-b border-black text-[8.5px]">
          <span>{POWER_METRIC_COPY.unitsHeaderName}</span>
          <span>{POWER_METRIC_COPY.unitsHeaderUnit}</span>
        </div>
      )}
      <HudHoverDetailBody className="flex gap-3">
        {regions.map((region) => (
          <div key={region.id} className="flex-1 min-w-0">
            <RegionGroupBlock
              region={region}
              regions={regions}
              hoveredItem={hoveredItem}
              emphasis={emphasis}
              itemHighlightDisabled={itemHighlightDisabled}
              hierarchy={hierarchy}
              maxLabRows={maxLabRows}
              totalSuffix={
                metricLabel === "value-units"
                  ? POWER_METRIC_COPY.valueUnitSuffix
                  : undefined
              }
            />
          </div>
        ))}
      </HudHoverDetailBody>
      {metricLabel === "footnote" && (
        <div className="font-mono mt-1.5 text-[8px]">
          {POWER_METRIC_COPY.footnote}
        </div>
      )}
    </div>
  )
}

// ─── Frontier-split power breakdown (the 2026-07-12 exploration) ────────────
// The breakdown split by FRONTIER ACCESS instead of geography: title,
// subtitle, then the companies with access to frontier models as one segment
// and everything else (individual non-frontier holders plus the "Rest of X"
// residuals) as "Others". Designs in PowerFrontierSplitDesign
// (powerHoverSwap.tsx); every editable value lives in the two dicts below.
export const POWER_FRONTIER_SPLIT_COPY = {
  frontierHeader: "Companies with access to frontier models",
  othersHeader: "Others",
  // "region-tags" design: the small geography tag after each company name.
  regionTagByRegionId: {
    america: "US",
    china: "China",
    world: "RoW",
  } as Record<PowerRegionId, string>,
}
export const POWER_FRONTIER_SPLIT_STYLE = {
  // Segment headers speak the hierarchy region-header voice: mono bold at
  // body size.
  headerFontSize: "9.5px",
  // Vertical gap between the two stacked segments.
  segmentGapPx: 10,
  // "by-country-outline-top-labels" (tab 31) and its tl-* lineage: tighter
  // band gap — with the Others outline gone, the section reads better
  // pulled 5px closer.
  topLabelsSegmentGapPx: 5,
  // Tab 31's lineage — the column-continuity treatments (one per design):
  // "tl-dividers": hairline vertical rules between the columns, drawn in
  // BOTH bands so the three columns read as continuous tracks.
  tlDividers: { widthPx: 1, color: "#000", gapToTextPx: 5 },
  // "tl-whisper-labels": the Others band repeats each country name, tiny —
  // de-emphasis by size, no total, not bold.
  tlWhisperLabel: { fontSize: "7.5px" },
  // "tl-labels-outside": the country headers move ABOVE the red box, owning
  // the whole column (name + the country's FULL total). sideInsetPx matches
  // the box's border+padding (1.5+6) so the header row's columns align with
  // the boxed columns below.
  tlLabelsOutside: { headerFontSize: "8.5px", sideInsetPx: 7.5, headerToBandGapPx: 3 },
  // "tl-hover-rail": hovering a country marks ITS column across both bands
  // with a red left rail — every column reserves the rail's space
  // (transparent), so nothing shifts when the hover travels.
  tlHoverRail: { widthPx: 3, gapPx: 6, color: "var(--accent)" },
  // "tl-rule": no box at all — a red rule under the frontier band delimits
  // it, so the columns read continuous top to bottom.
  tlRule: { ruleWidthPx: 2, color: "var(--accent)", gapPx: 4 },
  // Stacked designs flow each segment's rows in this many columns.
  columnCount: 3,
  columnGapPx: 16,
  // Per-segment row caps before "And N more" (total across the columns).
  frontierMaxRows: 12,
  othersMaxRows: 9,
  // "side-by-side": one column per segment.
  sideBySideMaxRows: 10,
  sideBySideGapPx: 24,
  // "red-rail": the frontier segment's mark — the DC-hover / power rail
  // language (thick red left rule, red header). The others segment reserves
  // the same inset (transparent) so both segments' text stays aligned.
  redRail: { widthPx: 3, gapPx: 6, color: "var(--accent)" },
  // "region-tags": geography tag size — de-emphasis by SIZE, full black.
  regionTagFontSize: "7.5px",
  // "by-country": each segment band holds the three country columns; the
  // region sub-headers sit a step below the segment header, each carrying
  // that region's segment total right-aligned.
  byCountry: {
    regionHeaderFontSize: "8.5px",
    columnGapPx: 12,
    // Gap between a segment's header line and its country columns.
    headerToColumnsGapPx: 3,
    frontierMaxRowsPerRegion: 6,
    othersMaxRowsPerRegion: 4,
  },
  // "by-country-outline": each segment BAND boxed in the treemap cells'
  // outline vocabulary — the frontier band outlined red, Others black (the
  // lower box a touch lighter at 1px).
  segmentOutline: {
    frontierBorderWidthPx: 1.5,
    othersBorderWidthPx: 1,
    frontierColor: "var(--accent)",
    othersColor: "#000",
    paddingPx: 6,
    borderRadiusPx: 3,
  },
  // "by-country-row-outline": every company ROW in its own small box —
  // frontier companies' boxes red, the others' black (the "And N more"
  // lines stay unboxed).
  rowOutline: {
    borderWidthPx: 1,
    frontierColor: "var(--accent)",
    othersColor: "#000",
    paddingX: 4,
    paddingY: 0,
    borderRadiusPx: 2,
    rowGapPx: 2,
  },
  // "by-country-row-marker": a small outline box BEFORE each row's name (a
  // treemap cell in miniature) — red 2px for frontier companies, black 1px
  // for the others (the "And N more" lines stay unmarked).
  rowLeadingBox: {
    squareSizePx: 7,
    frontierBorderWidthPx: 2,
    othersBorderWidthPx: 1,
    frontierColor: "var(--accent)",
    othersColor: "#000",
    borderRadiusPx: 2,
    gapPx: 5,
  },
}

// Tab 31's lineage ("tl" = top-labels): countries labeled once, the Others
// band aligned beneath — each design adds one way of making the Others rows
// read as part of the SAME column (Joanna 2026-07-12: "it's not clear
// enough that the countries in the other column are in the same column").
const isTopLabelsFamily = (design: PowerFrontierSplitDesign) =>
  design === "by-country-outline-top-labels" ||
  design === "by-country-tl-dividers" ||
  design === "by-country-tl-whisper-labels" ||
  design === "by-country-tl-labels-outside" ||
  design === "by-country-tl-hover-rail" ||
  design === "by-country-tl-rule"

// The by-country family: the frontier/others bands each hold the three
// country columns — the outline designs only add box treatments on top.
const isByCountryDesign = (design: PowerFrontierSplitDesign) =>
  design === "by-country" ||
  design === "by-country-outline" ||
  design === "by-country-row-outline" ||
  design === "by-country-row-marker" ||
  isTopLabelsFamily(design)

// The country-first restructure (tabs 28–30, 32–37): columns are per COUNTRY
// (labeled once), the frontier/others split lives INSIDE each column.
const isCountryColumnsDesign = (design: PowerFrontierSplitDesign) =>
  design === "country-columns-box" ||
  design === "country-columns-rail" ||
  design === "country-columns-headers" ||
  design === "country-columns-headers-hover" ||
  design === "country-columns-rail-key-right" ||
  design === "country-columns-rail-key-right-icons" ||
  design === "country-columns-rail-key-right-hover" ||
  isIconsUnderlineDesign(design)

// The rail sub-family: frontier groups marked with the thick red left rail.
const isCountryColumnsRailDesign = (design: PowerFrontierSplitDesign) =>
  design === "country-columns-rail" ||
  design === "country-columns-rail-key-right" ||
  design === "country-columns-rail-key-right-icons" ||
  design === "country-columns-rail-key-right-hover"

// Key placement: on the title line's right edge instead of a standalone
// legend line above the columns (Joanna 2026-07-12 — less header space).
const isKeyRightDesign = (design: PowerFrontierSplitDesign) =>
  design === "country-columns-rail-key-right" ||
  design === "country-columns-rail-key-right-icons" ||
  design === "country-columns-rail-key-right-hover" ||
  design === "country-columns-icons-underline" ||
  design === "country-columns-icons-underline-key-subtitle"

// The icons sub-family: frontier rows carry their company icon (and the key
// glyph includes it).
const isCountryColumnsIconsDesign = (design: PowerFrontierSplitDesign) =>
  design === "country-columns-rail-key-right-icons" ||
  isIconsUnderlineDesign(design)

// The icons-underline pair (tab 38 + its key-below take): no rail, red icons
// alone mark frontier access, hovered country marked by the rule under its
// header (rule ONLY — the name stays black), "Rest of X" rows don't red.
const isIconsUnderlineDesign = (design: PowerFrontierSplitDesign) =>
  design === "country-columns-icons-underline" ||
  design === "country-columns-icons-underline-key-below" ||
  design === "country-columns-icons-underline-key-subtitle-stacked" ||
  isKeySubtitleRowDesign(design)

// The subtitle-row pair: the key shares the measured-in line's row in its
// voice — key right ("-key-subtitle") or key left ("-swapped").
const isKeySubtitleRowDesign = (design: PowerFrontierSplitDesign) =>
  design === "country-columns-icons-underline-key-subtitle" ||
  design === "country-columns-icons-underline-key-subtitle-swapped"

// ─── Country-columns frontier breakdown (the 2026-07-12 tab-25 iteration) ───
// Three columns, one per country, each labeled ONCE (name + total at the
// top); the frontier companies are the column's top group — still called out
// as frontier access — and the others follow below. The box/rail designs
// explain their red mark with a one-line legend above the columns; the
// headers designs label the two groups inside every column instead.
export const POWER_COUNTRY_COLUMNS_COPY = {
  // Legend line above the columns ("-box" / "-rail" designs).
  legendLabel: "Companies with access to frontier models",
  // Per-column group labels ("-headers" designs).
  frontierGroupLabel: "Access to frontier models",
  othersGroupLabel: "Others",
}
export const POWER_COUNTRY_COLUMNS_STYLE = {
  // Country header: the segment-header voice (mono bold), name left, that
  // country's summed compute right.
  countryHeaderFontSize: "9.5px",
  columnGapPx: 16,
  // Gap between the country header and the frontier group under it.
  headerToRowsGapPx: 3,
  // Gap between a column's frontier group and its others group.
  groupGapPx: 6,
  frontierMaxRowsPerCountry: 6,
  othersMaxRowsPerCountry: 4,
  // "-box": the red outline box around each column's frontier group (the
  // treemap cells' outline vocabulary, per column).
  box: { borderWidthPx: 1.5, color: "var(--accent)", paddingPx: 4, borderRadiusPx: 3 },
  // "-rail": the thick red left rail down the frontier group (the DC-hover /
  // power rail language).
  rail: { widthPx: 3, gapPx: 6, color: "var(--accent)" },
  // "-headers": the per-column group labels — the frontier one red, Others
  // black; de-emphasis by SIZE per the palette rule.
  groupHeader: { fontSize: "8px", frontierColor: "var(--accent)", othersColor: "#000" },
  // The legend line ("-box" / "-rail"): a small glyph of the design's red
  // mark, then the wording.
  legend: { fontSize: "8.5px", glyphBoxSizePx: 7, glyphRailHeightPx: 9, gapPx: 5, marginBottomPx: 5 },
  // "-headers-hover": the hovered country's header reds in the pane while
  // its treemap group is red.
  hoveredCountryColor: "var(--accent)",
  // "-icons": the company icon before each frontier row's name (the
  // treemap's frontier mark, brought into the list; anonymous frontier
  // holders get the generic AI-company mark so icon presence = frontier
  // access holds for every row). Also drawn in the key's glyph.
  rowIcon: { sizePx: 9, color: "var(--accent)", gapPx: 4, verticalAlignPx: -1.5 },
  // "-icons-underline": the hovered country's mark — tab 0's horizontal-line
  // vocabulary: a red rule under its column header, header text red. Every
  // header reserves the rule's space (transparent) so columns never shift.
  hoveredUnderline: { ruleWidthPx: 2, color: "var(--accent)", gapPx: 2 },
  // "-key-subtitle": the key in the SUBTITLE's voice — the size matches the
  // measured-in line's tier ("smaller-type" description, 11px sans, normal
  // weight — see HUD_HOVER_HEADER_TREATMENTS in hudHoverDetailHeader.tsx).
  // iconNudgeLeftPx pulls the key's icon left so its visual edge (the glyph
  // draws inset from its viewBox) lines up with the title above.
  keySubtitleVoice: { fontSize: "11px", iconNudgeLeftPx: -1 },
}

// One country column's frontier or others group, marked per the design.
const CountryColumnGroup = ({
  labs,
  hoveredItem,
  design,
  isFrontierGroup,
}: {
  labs: PowerLabData[]
  hoveredItem: HoveredPowerItem
  design: PowerFrontierSplitDesign
  isFrontierGroup: boolean
}) => {
  const showGroupHeaders =
    design === "country-columns-headers" || design === "country-columns-headers-hover"
  const markStyle = !isFrontierGroup
    ? undefined
    : design === "country-columns-box"
      ? {
          border: `${POWER_COUNTRY_COLUMNS_STYLE.box.borderWidthPx}px solid ${POWER_COUNTRY_COLUMNS_STYLE.box.color}`,
          borderRadius: POWER_COUNTRY_COLUMNS_STYLE.box.borderRadiusPx,
          padding: POWER_COUNTRY_COLUMNS_STYLE.box.paddingPx,
        }
      : isCountryColumnsRailDesign(design)
        ? {
            borderLeft: `${POWER_COUNTRY_COLUMNS_STYLE.rail.widthPx}px solid ${POWER_COUNTRY_COLUMNS_STYLE.rail.color}`,
            paddingLeft: POWER_COUNTRY_COLUMNS_STYLE.rail.gapPx,
          }
        : undefined
  // "-icons": frontier rows carry their company icon before the name —
  // anonymous holders get the generic mark, so every frontier row is marked.
  const renderIconName =
    isCountryColumnsIconsDesign(design) && isFrontierGroup
      ? (lab: PowerLabData) => {
          const Icon = labIconById[lab.id as PowerLabId] ?? genericFrontierLabIcon
          return (
            <>
              <Icon
                size={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.sizePx}
                color={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.color}
                style={{
                  display: "inline-block",
                  verticalAlign: POWER_COUNTRY_COLUMNS_STYLE.rowIcon.verticalAlignPx,
                  marginRight: POWER_COUNTRY_COLUMNS_STYLE.rowIcon.gapPx,
                }}
              />
              {lab.name}
            </>
          )
        }
      : undefined
  return (
    <div style={markStyle}>
      {showGroupHeaders && (
        <div
          className="font-mono font-bold"
          style={{
            fontSize: POWER_COUNTRY_COLUMNS_STYLE.groupHeader.fontSize,
            color: isFrontierGroup
              ? POWER_COUNTRY_COLUMNS_STYLE.groupHeader.frontierColor
              : POWER_COUNTRY_COLUMNS_STYLE.groupHeader.othersColor,
          }}
        >
          {isFrontierGroup
            ? POWER_COUNTRY_COLUMNS_COPY.frontierGroupLabel
            : POWER_COUNTRY_COLUMNS_COPY.othersGroupLabel}
        </div>
      )}
      {labs.length === 0 ? (
        <div
          className="font-sans"
          style={{ fontSize: POWER_BREAKDOWN_HIERARCHY_STYLE.rowFontSize }}
        >
          {POLICY_LIST_EMPTY_SECTION_MARK}
        </div>
      ) : (
        <CappedLabList
          labs={labs}
          hoveredItem={hoveredItem}
          maxRows={
            isFrontierGroup
              ? POWER_COUNTRY_COLUMNS_STYLE.frontierMaxRowsPerCountry
              : POWER_COUNTRY_COLUMNS_STYLE.othersMaxRowsPerCountry
          }
          sansBody
          renderName={renderIconName}
        />
      )}
    </div>
  )
}

// The full country-columns detail: title, measured-in subtitle, the legend
// line (box/rail designs), then one column per country.
const PowerHoverCountryColumnsBreakdown = ({
  hoveredItem,
  design,
}: {
  hoveredItem: HoveredPowerItem
  design: PowerFrontierSplitDesign
}) => {
  const regions = usePowerRegions()
  // "-headers-hover" / "-key-right-hover" / "-icons-underline": the hovered
  // country marks in the pane too (red header; the underline design adds
  // the tab-0 rule).
  const hoveredRegionId =
    design === "country-columns-headers-hover" ||
    design === "country-columns-rail-key-right-hover" ||
    isIconsUnderlineDesign(design)
      ? getHoveredRegionId(hoveredItem, regions)
      : null
  const byComputeDesc = (a: PowerLabData, b: PowerLabData) => b.compute - a.compute
  const keyRight = isKeyRightDesign(design)
  const showStandaloneLegend =
    design === "country-columns-box" ||
    design === "country-columns-rail" ||
    // "-key-below": the icon key sits UNDER the measured-in subheader as
    // its own line (Joanna 2026-07-12), instead of on the title line.
    design === "country-columns-icons-underline-key-below"
  // The key's content — the design's red mark as a glyph, then the wording.
  // Rendered as a standalone line above the columns (box / plain rail) or on
  // the title line's right edge (the "-key-right" designs).
  const GenericIcon = genericFrontierLabIcon
  const keyContent = (
    <>
      {design === "country-columns-box" ? (
        <span
          className="shrink-0"
          style={{
            width: POWER_COUNTRY_COLUMNS_STYLE.legend.glyphBoxSizePx,
            height: POWER_COUNTRY_COLUMNS_STYLE.legend.glyphBoxSizePx,
            border: `${POWER_COUNTRY_COLUMNS_STYLE.box.borderWidthPx}px solid ${POWER_COUNTRY_COLUMNS_STYLE.box.color}`,
            borderRadius: 2,
            boxSizing: "border-box",
          }}
        />
      ) : isIconsUnderlineDesign(design) ? null : (
        // The icons-underline pair has no rail — the icon alone is the
        // key's glyph.
        <span
          className="shrink-0"
          style={{
            width: POWER_COUNTRY_COLUMNS_STYLE.rail.widthPx,
            height: POWER_COUNTRY_COLUMNS_STYLE.legend.glyphRailHeightPx,
            background: POWER_COUNTRY_COLUMNS_STYLE.rail.color,
          }}
        />
      )}
      {isCountryColumnsIconsDesign(design) && (
        <GenericIcon
          size={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.sizePx}
          color={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.color}
          className="shrink-0"
        />
      )}
      <span className="min-w-0">{POWER_COUNTRY_COLUMNS_COPY.legendLabel}</span>
    </>
  )
  return (
    <div>
      {isKeySubtitleRowDesign(design) ? (
        // The subtitle-row pair: the key shares the SUBTITLE's row —
        // baseline-aligned with the measured-in line (both 11px sans),
        // title alone above. "-swapped" puts the key LEFT and the
        // measured-in line right.
        <>
          <HudHoverTitle>{POWER_HOVER_TITLE}</HudHoverTitle>
          <div
            className={`flex items-baseline justify-between gap-3 ${
              design === "country-columns-icons-underline-key-subtitle-swapped"
                ? "flex-row-reverse"
                : ""
            }`}
          >
            <HudHoverDescription>{POWER_METRIC_COPY.measuredInline}</HudHoverDescription>
            <div
              className="font-sans whitespace-nowrap"
              style={{ fontSize: POWER_COUNTRY_COLUMNS_STYLE.keySubtitleVoice.fontSize }}
            >
              <GenericIcon
                size={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.sizePx}
                color={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.color}
                style={{
                  display: "inline-block",
                  verticalAlign: POWER_COUNTRY_COLUMNS_STYLE.rowIcon.verticalAlignPx,
                  marginLeft: POWER_COUNTRY_COLUMNS_STYLE.keySubtitleVoice.iconNudgeLeftPx,
                  marginRight: POWER_COUNTRY_COLUMNS_STYLE.legend.gapPx,
                }}
              />
              {POWER_COUNTRY_COLUMNS_COPY.legendLabel}
            </div>
          </div>
        </>
      ) : keyRight ? (
        <div className="flex items-baseline justify-between gap-3">
          <HudHoverTitle>{POWER_HOVER_TITLE}</HudHoverTitle>
          {isIconsUnderlineDesign(design) ? (
            // Icon-only key as ONE text flow (icon inline, vertical-align
            // like the row icons): the div's baseline is the LABEL's
            // baseline, so the outer items-baseline truly baseline-aligns
            // the key with the title — a flex key's baseline would come
            // from the icon's bottom edge instead. Not bold (Joanna
            // 2026-07-12) — the red icon carries the key's weight.
            // ("-key-subtitle" never reaches this branch — it renders the
            // key on the SUBTITLE's row, in the block above.)
            <div
              className="font-mono whitespace-nowrap"
              style={{ fontSize: POWER_COUNTRY_COLUMNS_STYLE.legend.fontSize }}
            >
              <GenericIcon
                size={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.sizePx}
                color={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.color}
                style={{
                  display: "inline-block",
                  verticalAlign: POWER_COUNTRY_COLUMNS_STYLE.rowIcon.verticalAlignPx,
                  marginRight: POWER_COUNTRY_COLUMNS_STYLE.legend.gapPx,
                }}
              />
              {POWER_COUNTRY_COLUMNS_COPY.legendLabel}
            </div>
          ) : (
            <div
              className="flex items-center font-mono font-bold"
              style={{
                fontSize: POWER_COUNTRY_COLUMNS_STYLE.legend.fontSize,
                gap: POWER_COUNTRY_COLUMNS_STYLE.legend.gapPx,
              }}
            >
              {keyContent}
            </div>
          )}
        </div>
      ) : (
        <HudHoverTitle>{POWER_HOVER_TITLE}</HudHoverTitle>
      )}
      {/* The subtitle-row pair already rendered the subtitle in its row. */}
      {!isKeySubtitleRowDesign(design) && (
        <HudHoverDescription>{POWER_METRIC_COPY.measuredInline}</HudHoverDescription>
      )}
      {/* "-key-subtitle-stacked": the subtitle-voice key on its own line,
          STACKED directly under the measured-in line. */}
      {design === "country-columns-icons-underline-key-subtitle-stacked" && (
        <div
          className="font-sans whitespace-nowrap"
          style={{
            fontSize: POWER_COUNTRY_COLUMNS_STYLE.keySubtitleVoice.fontSize,
            marginBottom: POWER_COUNTRY_COLUMNS_STYLE.legend.marginBottomPx,
          }}
        >
          <GenericIcon
            size={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.sizePx}
            color={POWER_COUNTRY_COLUMNS_STYLE.rowIcon.color}
            style={{
              display: "inline-block",
              verticalAlign: POWER_COUNTRY_COLUMNS_STYLE.rowIcon.verticalAlignPx,
              marginRight: POWER_COUNTRY_COLUMNS_STYLE.legend.gapPx,
            }}
          />
          {POWER_COUNTRY_COLUMNS_COPY.legendLabel}
        </div>
      )}
      {showStandaloneLegend && (
        <div
          // The icons-underline pair's key is not bold — the red icon
          // carries the key's weight; the box/rail legends stay bold.
          className={`flex items-center font-mono ${isIconsUnderlineDesign(design) ? "" : "font-bold"}`}
          style={{
            fontSize: POWER_COUNTRY_COLUMNS_STYLE.legend.fontSize,
            gap: POWER_COUNTRY_COLUMNS_STYLE.legend.gapPx,
            marginBottom: POWER_COUNTRY_COLUMNS_STYLE.legend.marginBottomPx,
          }}
        >
          {keyContent}
        </div>
      )}
      <HudHoverDetailBody
        className="flex"
        style={{ gap: POWER_COUNTRY_COLUMNS_STYLE.columnGapPx }}
      >
        {regions.map((region) => {
          const labs = region.labs.filter((lab) => lab.compute > 0)
          const frontier = labs.filter((lab) => !lab.borderOnly).sort(byComputeDesc)
          const others = labs.filter((lab) => lab.borderOnly).sort(byComputeDesc)
          const total = labs.reduce((sum, lab) => sum + lab.compute, 0)
          return (
            <div key={region.id} className="flex-1 min-w-0">
              <div
                className="flex items-baseline justify-between gap-2 font-mono font-bold"
                style={{
                  fontSize: POWER_COUNTRY_COLUMNS_STYLE.countryHeaderFontSize,
                  // The underline design marks the hovered country with the
                  // rule ALONE — the name stays black (Joanna 2026-07-12:
                  // the line and the red name together are redundant).
                  color:
                    hoveredRegionId === region.id &&
                    !isIconsUnderlineDesign(design)
                      ? POWER_COUNTRY_COLUMNS_STYLE.hoveredCountryColor
                      : undefined,
                  // "-icons-underline": tab 0's horizontal line under the
                  // hovered country's header. EVERY header reserves the
                  // rule's space (transparent) so columns never shift.
                  ...(isIconsUnderlineDesign(design) && {
                    borderBottom: `${POWER_COUNTRY_COLUMNS_STYLE.hoveredUnderline.ruleWidthPx}px solid ${
                      hoveredRegionId === region.id
                        ? POWER_COUNTRY_COLUMNS_STYLE.hoveredUnderline.color
                        : "transparent"
                    }`,
                    paddingBottom: POWER_COUNTRY_COLUMNS_STYLE.hoveredUnderline.gapPx,
                    transition: "border-color 160ms ease",
                  }),
                }}
              >
                <span className="min-w-0">{region.name}</span>
                <span className="whitespace-nowrap tabular-nums">{formatCompact(total)}</span>
              </div>
              {/* Countries with no frontier companies skip the frontier
                  group outright — no boxed/railed "—" placeholder
                  (Joanna 2026-07-12); their others rows just start higher. */}
              {frontier.length > 0 && (
                <div style={{ marginTop: POWER_COUNTRY_COLUMNS_STYLE.headerToRowsGapPx }}>
                  <CountryColumnGroup
                    labs={frontier}
                    hoveredItem={hoveredItem}
                    design={design}
                    isFrontierGroup
                  />
                </div>
              )}
              <div style={{ marginTop: POWER_COUNTRY_COLUMNS_STYLE.groupGapPx }}>
                <CountryColumnGroup
                  labs={others}
                  hoveredItem={hoveredItem}
                  design={design}
                  isFrontierGroup={false}
                />
              </div>
            </div>
          )
        })}
      </HudHoverDetailBody>
    </div>
  )
}

// One segment: header line (name + optional summed total), then the capped
// rows — columnar in the stacked designs, a single list side-by-side.
const FrontierSplitSegment = ({
  header,
  labs,
  hoveredItem,
  design,
  isFrontierSegment = false,
  byCountryColumns,
  hoveredRegionId = null,
}: {
  header: string
  labs: PowerLabData[]
  hoveredItem: HoveredPowerItem
  design: PowerFrontierSplitDesign
  isFrontierSegment?: boolean
  // "by-country": the segment's rows organized as three country columns
  // (region sub-header with that region's segment total, then its rows).
  byCountryColumns?: { region: PowerRegionData; labs: PowerLabData[] }[]
  // "tl-hover-rail": the country whose treemap group is hovered — its
  // column takes the red rail in both bands.
  hoveredRegionId?: PowerRegionId | null
}) => {
  const sideBySide = design === "side-by-side"
  const maxRows = sideBySide
    ? POWER_FRONTIER_SPLIT_STYLE.sideBySideMaxRows
    : isFrontierSegment
      ? POWER_FRONTIER_SPLIT_STYLE.frontierMaxRows
      : POWER_FRONTIER_SPLIT_STYLE.othersMaxRows
  const railStyle =
    design === "red-rail"
      ? {
          borderLeft: `${POWER_FRONTIER_SPLIT_STYLE.redRail.widthPx}px solid ${
            isFrontierSegment ? POWER_FRONTIER_SPLIT_STYLE.redRail.color : "transparent"
          }`,
          paddingLeft: POWER_FRONTIER_SPLIT_STYLE.redRail.gapPx,
        }
      : design === "by-country-tl-rule"
        ? // "tl-rule": no box — the frontier band is delimited by a red rule
          // under it, so the columns read continuous top to bottom.
          isFrontierSegment
            ? {
                borderBottom: `${POWER_FRONTIER_SPLIT_STYLE.tlRule.ruleWidthPx}px solid ${POWER_FRONTIER_SPLIT_STYLE.tlRule.color}`,
                paddingBottom: POWER_FRONTIER_SPLIT_STYLE.tlRule.gapPx,
              }
            : undefined
        : design === "by-country-outline" || isTopLabelsFamily(design)
          ? {
              // "top-labels" (tab 31) and its tl-* lineage: only the frontier
              // band draws its box — the Others band's border goes
              // TRANSPARENT (same width kept, so its columns stay aligned
              // under the frontier band's).
              border: isFrontierSegment
                ? `${POWER_FRONTIER_SPLIT_STYLE.segmentOutline.frontierBorderWidthPx}px solid ${POWER_FRONTIER_SPLIT_STYLE.segmentOutline.frontierColor}`
                : `${POWER_FRONTIER_SPLIT_STYLE.segmentOutline.othersBorderWidthPx}px solid ${
                    isTopLabelsFamily(design)
                      ? "transparent"
                      : POWER_FRONTIER_SPLIT_STYLE.segmentOutline.othersColor
                  }`,
              borderRadius: POWER_FRONTIER_SPLIT_STYLE.segmentOutline.borderRadiusPx,
              padding: POWER_FRONTIER_SPLIT_STYLE.segmentOutline.paddingPx,
            }
          : undefined
  return (
    <div className={sideBySide ? "flex-1 min-w-0" : undefined} style={railStyle}>
      <div
        className="flex items-baseline justify-between gap-2 font-mono font-bold"
        style={{
          fontSize: POWER_FRONTIER_SPLIT_STYLE.headerFontSize,
          color:
            design === "red-rail" && isFrontierSegment
              ? POWER_FRONTIER_SPLIT_STYLE.redRail.color
              : undefined,
        }}
      >
        <span className="min-w-0">{header}</span>
        {design === "segment-totals" && (
          <span className="whitespace-nowrap">
            {formatCompact(labs.reduce((sum, lab) => sum + lab.compute, 0))}
          </span>
        )}
      </div>
      {isByCountryDesign(design) && byCountryColumns ? (
        <div
          className="flex"
          style={{
            gap: POWER_FRONTIER_SPLIT_STYLE.byCountry.columnGapPx,
            marginTop: POWER_FRONTIER_SPLIT_STYLE.byCountry.headerToColumnsGapPx,
          }}
        >
          {byCountryColumns.map(({ region, labs: regionLabs }, columnIndex) => {
            // The top-labels lineage labels each country ONCE: the frontier
            // band carries the sub-headers ("tl-labels-outside" moves even
            // those above the box); the Others band is unlabeled — except
            // "tl-whisper-labels", which repeats the name tiny.
            const whisper =
              design === "by-country-tl-whisper-labels" && !isFrontierSegment
            const showSubHeader = isFrontierSegment
              ? design !== "by-country-tl-labels-outside"
              : !isTopLabelsFamily(design) || whisper
            // "tl-dividers" / "tl-hover-rail": per-column left edges — a
            // hairline between columns in both bands, or the reserved
            // hover rail on every column.
            const columnStyle =
              design === "by-country-tl-dividers" && columnIndex > 0
                ? {
                    borderLeft: `${POWER_FRONTIER_SPLIT_STYLE.tlDividers.widthPx}px solid ${POWER_FRONTIER_SPLIT_STYLE.tlDividers.color}`,
                    paddingLeft: POWER_FRONTIER_SPLIT_STYLE.tlDividers.gapToTextPx,
                  }
                : design === "by-country-tl-hover-rail"
                  ? {
                      borderLeft: `${POWER_FRONTIER_SPLIT_STYLE.tlHoverRail.widthPx}px solid ${
                        hoveredRegionId === region.id
                          ? POWER_FRONTIER_SPLIT_STYLE.tlHoverRail.color
                          : "transparent"
                      }`,
                      paddingLeft: POWER_FRONTIER_SPLIT_STYLE.tlHoverRail.gapPx,
                      transition: "border-color 160ms ease",
                    }
                  : undefined
            return (
            <div key={region.id} className="flex-1 min-w-0" style={columnStyle}>
              {showSubHeader && (
                whisper ? (
                  <div
                    className="font-mono"
                    style={{ fontSize: POWER_FRONTIER_SPLIT_STYLE.tlWhisperLabel.fontSize }}
                  >
                    {region.name}
                  </div>
                ) : (
                <div
                  className="flex items-baseline justify-between gap-2 font-mono font-bold"
                  style={{ fontSize: POWER_FRONTIER_SPLIT_STYLE.byCountry.regionHeaderFontSize }}
                >
                  <span className="min-w-0">{region.name}</span>
                  <span className="whitespace-nowrap tabular-nums">
                    {formatCompact(regionLabs.reduce((sum, lab) => sum + lab.compute, 0))}
                  </span>
                </div>
                )
              )}
              <div className="mt-0.5">
                {regionLabs.length === 0 ? (
                  <div
                    className="font-sans"
                    style={{ fontSize: POWER_BREAKDOWN_HIERARCHY_STYLE.rowFontSize }}
                  >
                    {POLICY_LIST_EMPTY_SECTION_MARK}
                  </div>
                ) : (
                  <CappedLabList
                    labs={regionLabs}
                    hoveredItem={hoveredItem}
                    maxRows={
                      isFrontierSegment
                        ? POWER_FRONTIER_SPLIT_STYLE.byCountry.frontierMaxRowsPerRegion
                        : POWER_FRONTIER_SPLIT_STYLE.byCountry.othersMaxRowsPerRegion
                    }
                    sansBody
                    rowBox={
                      design === "by-country-row-outline"
                        ? {
                            ...POWER_FRONTIER_SPLIT_STYLE.rowOutline,
                            borderColor: isFrontierSegment
                              ? POWER_FRONTIER_SPLIT_STYLE.rowOutline.frontierColor
                              : POWER_FRONTIER_SPLIT_STYLE.rowOutline.othersColor,
                          }
                        : undefined
                    }
                    rowLeadingBox={
                      design === "by-country-row-marker"
                        ? {
                            ...POWER_FRONTIER_SPLIT_STYLE.rowLeadingBox,
                            borderWidthPx: isFrontierSegment
                              ? POWER_FRONTIER_SPLIT_STYLE.rowLeadingBox.frontierBorderWidthPx
                              : POWER_FRONTIER_SPLIT_STYLE.rowLeadingBox.othersBorderWidthPx,
                            borderColor: isFrontierSegment
                              ? POWER_FRONTIER_SPLIT_STYLE.rowLeadingBox.frontierColor
                              : POWER_FRONTIER_SPLIT_STYLE.rowLeadingBox.othersColor,
                          }
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
            )
          })}
        </div>
      ) : (
        <div
          className="mt-0.5"
          style={
            sideBySide
              ? undefined
              : {
                  columns: POWER_FRONTIER_SPLIT_STYLE.columnCount,
                  columnGap: POWER_FRONTIER_SPLIT_STYLE.columnGapPx,
                }
          }
        >
          <CappedLabList
            labs={labs}
            hoveredItem={hoveredItem}
            maxRows={maxRows}
            sansBody
            renderName={
              design === "region-tags"
                ? (lab) => (
                    <>
                      {lab.name}
                      <span
                        className="font-mono"
                        style={{ fontSize: POWER_FRONTIER_SPLIT_STYLE.regionTagFontSize }}
                      >
                        {" "}
                        {POWER_FRONTIER_SPLIT_COPY.regionTagByRegionId[lab.regionId]}
                      </span>
                    </>
                  )
                : undefined
            }
          />
        </div>
      )}
    </div>
  )
}

// The full frontier-split detail: main title, the measured-in subtitle, then
// the two segments (see POWER_FRONTIER_SPLIT_COPY).
const PowerHoverFrontierSplitBreakdown = ({
  hoveredItem,
  design,
}: {
  hoveredItem: HoveredPowerItem
  design: PowerFrontierSplitDesign
}) => {
  const regions = usePowerRegions()
  const labsWithCompute = regions.flatMap((region) =>
    region.labs.filter((lab) => lab.compute > 0),
  )
  const byComputeDesc = (a: PowerLabData, b: PowerLabData) => b.compute - a.compute
  const frontier = labsWithCompute.filter((lab) => !lab.borderOnly).sort(byComputeDesc)
  const others = labsWithCompute.filter((lab) => lab.borderOnly).sort(byComputeDesc)
  // "by-country": the same split, kept organized by region inside each band.
  const regionColumnsOf = (segmentLabs: PowerLabData[]) =>
    isByCountryDesign(design)
      ? regions.map((region) => ({
          region,
          labs: segmentLabs.filter((lab) => lab.regionId === region.id),
        }))
      : undefined
  const sideBySide = design === "side-by-side"
  // "tl-hover-rail": the hovered country's column takes the red rail.
  const hoveredRegionId =
    design === "by-country-tl-hover-rail"
      ? getHoveredRegionId(hoveredItem, regions)
      : null
  return (
    <div>
      <HudHoverTitle>{POWER_HOVER_TITLE}</HudHoverTitle>
      <HudHoverDescription>{POWER_METRIC_COPY.measuredInline}</HudHoverDescription>
      {/* "tl-labels-outside": the country headers own the whole column —
          one row above BOTH bands (name + the country's full total), inset
          to align with the boxed columns below. */}
      {design === "by-country-tl-labels-outside" && (
        <div
          className="flex"
          style={{
            gap: POWER_FRONTIER_SPLIT_STYLE.byCountry.columnGapPx,
            padding: `0 ${POWER_FRONTIER_SPLIT_STYLE.tlLabelsOutside.sideInsetPx}px`,
            marginBottom: POWER_FRONTIER_SPLIT_STYLE.tlLabelsOutside.headerToBandGapPx,
          }}
        >
          {regions.map((region) => (
            <div
              key={region.id}
              className="flex-1 min-w-0 flex items-baseline justify-between gap-2 font-mono font-bold"
              style={{ fontSize: POWER_FRONTIER_SPLIT_STYLE.tlLabelsOutside.headerFontSize }}
            >
              <span className="min-w-0">{region.name}</span>
              <span className="whitespace-nowrap tabular-nums">
                {formatCompact(
                  region.labs
                    .filter((lab) => lab.compute > 0)
                    .reduce((sum, lab) => sum + lab.compute, 0),
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      <HudHoverDetailBody
        className={sideBySide ? "flex" : "flex flex-col"}
        style={{
          gap: sideBySide
            ? POWER_FRONTIER_SPLIT_STYLE.sideBySideGapPx
            : isTopLabelsFamily(design)
              ? POWER_FRONTIER_SPLIT_STYLE.topLabelsSegmentGapPx
              : POWER_FRONTIER_SPLIT_STYLE.segmentGapPx,
        }}
      >
        <FrontierSplitSegment
          header={POWER_FRONTIER_SPLIT_COPY.frontierHeader}
          labs={frontier}
          hoveredItem={hoveredItem}
          design={design}
          isFrontierSegment
          byCountryColumns={regionColumnsOf(frontier)}
          hoveredRegionId={hoveredRegionId}
        />
        <FrontierSplitSegment
          header={POWER_FRONTIER_SPLIT_COPY.othersHeader}
          labs={others}
          hoveredItem={hoveredItem}
          design={design}
          byCountryColumns={regionColumnsOf(others)}
          hoveredRegionId={hoveredRegionId}
        />
      </HudHoverDetailBody>
    </div>
  )
}

// Wraps the block BELOW the policies/treemap row (the labor strip). Instead
// of the tab-15 model — details replacing the left column or the treemap —
// every hover detail renders HERE, in the space the hidden labor strip frees
// up: a hovered policy square shows its detail, a stat-row metric its
// definition, and the treemap the three-column power breakdown. Both the
// (hidden) labor strip and the detail occupy the SAME grid cell, so the
// detail paints over the strip's footprint but still contributes real layout
// height — the enclosing AnimateYear wrapper (overflow-hidden, height:auto)
// grows to fit instead of clipping a taller detail. Nothing above the slot
// ever moves. When more than one family is set, policy wins over metric wins
// over power (the arrival-side cross-clears normally keep them exclusive).
export const BelowHudDetailSwapTarget = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const policySwap = usePolicyHoverSwap()
  const metricSwap = useMetricHoverSwap()
  const powerSwap = usePowerHoverSwap()
  // Tabs 1–5: this slot (the labor section) is itself a hover TARGET — the
  // wrapper below reports enter/leave to the labor swap. The section stays
  // visible (marked by LaborHoverSectionHighlight); the detail replaces the
  // policies/dist-of-power row above (LaborHoverRowTarget), not this slot.
  const laborSwap = useLaborHoverSwap()
  const sequentialFade = useHoverSwapSequentialFade()
  // Non-stock rule treatments (2026-07-11) change the slot's geometry too:
  // the detail starts exactly where the labor strip starts (no raise, no top
  // padding) and the rule hangs centered in the strip's mt-6 gap above.
  const stockRule = useContext(HudHoverRuleTreatmentContext) === "stock"
  const hoveredPolicyId = policySwap?.hoveredPolicyId ?? null
  const hoveredStatus = policySwap?.hoveredStatus ?? null
  const hoveredMetric = metricSwap?.hoveredMetric ?? null
  const hoveredPowerItem = powerSwap?.hoveredItem ?? null
  // Tabs 7–11: with a map design set, EVERY policy-area hover (a square or a
  // status label) renders the all-policies map with the hovered item
  // highlighted in place — instead of the single detail / by-section list.
  const policyMapDesign = policySwap?.mapDesign
  const detail = policyMapDesign && (hoveredPolicyId || hoveredStatus) ? (
    <PolicyMapHoverDetail
      design={policyMapDesign}
      hoveredPolicyId={hoveredPolicyId}
      hoveredStatus={hoveredStatus}
    />
  ) : hoveredPolicyId ? (
    <PolicyHoverDetail policyId={hoveredPolicyId} />
  ) : hoveredStatus ? (
    <PoliciesBySectionColumns hoveredStatus={hoveredStatus} />
  ) : hoveredMetric ? (
    // No value line here — the stat row with the live number stays visible
    // right above this slot in the below-detail variant. Clicking the metric
    // opens its full details panel, so the detail advertises it.
    <>
      <MetricHoverDetailContent hoveredMetric={hoveredMetric} hideValue />
      <HudHoverClickHint />
    </>
  ) : hoveredPowerItem ? (
    // Clicking the treemap opens the power details panel (MetricTrigger
    // id="power"), so this detail advertises it too.
    <>
      {powerSwap?.breakdownFrontierSplit ? (
        isCountryColumnsDesign(powerSwap.breakdownFrontierSplit) ? (
          <PowerHoverCountryColumnsBreakdown
            hoveredItem={hoveredPowerItem}
            design={powerSwap.breakdownFrontierSplit}
          />
        ) : (
          <PowerHoverFrontierSplitBreakdown
            hoveredItem={hoveredPowerItem}
            design={powerSwap.breakdownFrontierSplit}
          />
        )
      ) : (
        <PowerHoverThreeColumnBreakdown hoveredItem={hoveredPowerItem} />
      )}
      <HudHoverClickHint />
    </>
  ) : null
  // Identity of the shown detail: switching it runs the staggered handoff —
  // old detail fading out while the new one enters on the choreography's
  // delay (HoverSwapFadeOverlay's contentKey). The whole power breakdown is
  // ONE identity — cell-to-cell highlight changes update live without
  // refading. The policy map likewise: square-to-square (and square-to-label)
  // travel just moves the highlight, no refade.
  const detailKey = policyMapDesign && (hoveredPolicyId || hoveredStatus)
    ? "policy-map"
    : hoveredPolicyId
      ? `policy:${hoveredPolicyId}`
      : hoveredStatus
        ? `status:${hoveredStatus}`
        : hoveredMetric
          ? `metric:${hoveredMetric.id}`
          : hoveredPowerItem
            ? "power"
            : undefined
  return (
    <div
      className={`grid ${className ?? ""}`}
      // Labor-section hover (tabs 1–5): the slot is its own hover target.
      // Plain enter/leave — the overlay is pointer-events-none, so the
      // pointer stays over this wrapper the whole time the detail shows.
      onMouseEnter={laborSwap ? () => laborSwap.setHovered(true) : undefined}
      onMouseLeave={laborSwap ? () => laborSwap.setHovered(false) : undefined}
    >
      <div
        style={{
          gridArea: "1 / 1",
          // min-width 0 breaks a grid-blowout feedback loop on narrow
          // (mobile-dashboard) columns: the labor dot canvas writes an
          // explicit pixel width, which — through this item's default
          // min-width:auto — held the implicit track wider than the grid
          // itself, so the whole labor block (and its right-aligned "xN
          // speed") overhung the column and got clipped. With 0 the track
          // shrinks to the real column, the canvas's ResizeObserver sees
          // the smaller container, and it re-renders to fit.
          minWidth: 0,
          ...hoverSwapHiddenContentStyle(detail !== null, sequentialFade),
        }}
      >
        {children}
      </div>
      <HoverSwapFadeOverlay
        active={detail !== null}
        contentKey={detailKey}
        className="pointer-events-none"
        style={{
          gridArea: "1 / 1",
          minWidth: 0,
          transform: stockRule
            ? `translateY(-${BELOW_HUD_DETAIL_RAISE_PX}px)`
            : undefined,
        }}
      >
        {/* The rule lives INSIDE the overlay so it rides the detail's fade
            and its item-to-item handoffs. Stock: the air ABOVE the rule comes
            from the slot's margin (the mt-6 in variants.tsx) plus this
            wrapper's pt-4 — the padding lives here, not on the slot, so the
            extra air only exists while a hover detail shows and the labor
            strip keeps its stock mt-6 gap. Non-stock treatments: the detail
            starts flush at the strip's top edge and the rule hangs centered
            in the mt-6 gap (HUD_HOVER_RULE_CENTERED_TOP_PX). The details'
            13px title/description tier lives in the shared HudHoverTitle /
            HudHoverDescription components (hudHoverDetailHeader.tsx); the
            small data tables (power region columns, policy map, by-section
            list) keep their own stock sizes. */}
        {stockRule ? (
          <div className="relative pt-4">
            <HudHoverDetailRule className="absolute inset-x-0 -top-[2px]" />
            {detail}
          </div>
        ) : (
          <div
            className="relative"
            style={{ paddingTop: BELOW_HUD_DETAIL_TOP_NUDGE_PX }}
          >
            {/* The hung rule anchors to the wrapper's top edge, not the
                padding — the nudge moves the content only. */}
            <HudHoverDetailRule
              className="absolute inset-x-0"
              style={{ top: HUD_HOVER_RULE_CENTERED_TOP_PX }}
            />
            {detail}
          </div>
        )}
      </HoverSwapFadeOverlay>
    </div>
  )
}
